// src/components/surf/SurfWidget.jsx
//
// In-platform Surf — the floating fox button that lives on every
// authenticated page, and the full-screen overlay it summons. Same brand
// language as the v2 onboarding (HexBackground, Particles, fox-float,
// streaming cursor) so a tenant who met Surf at onboarding instantly
// recognizes him.
//
// V1 scope: chat-only. No tool use. When tools land (add_to_kb,
// buy_phone_number, create_campaign), they layer into runChatTurn below
// without changing the UI shape.
//
// Hidden on /onboarding and /onboarding-v2 — those routes own their own
// Surf experience and a second Surf would step on them.

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, X, RotateCcw } from 'lucide-react';
import { fetchWithCsrf } from '../../lib/fetchWithCsrf';
import { useAuth } from '../../context/AuthContext';
import DemoSurfPanel from './DemoSurfPanel';

const CHAR_STREAM_MS = 22;
const TYPING_BEFORE_MS = 600;
const HARDCODED_OPENER =
  "Hey, two things I can help with: buying a phone number or spinning up a new campaign (texting, web chat, voice, or Facebook Messenger). Which one?";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Surf turns can run a minute or two. A relative '/api/...' call is served by Vercel and proxied to Render by the rewrite in
// vercel.json, and Vercel CUTS that proxy at 120s with a plain-text 502 (ROUTER_EXTERNAL_TARGET_ERROR), which this widget can only
// show as "Surf lost the connection" while the server finishes and saves the turn anyway. So call the API host directly, the same
// REACT_APP_API_URL every other page uses (the API allows this origin and the x-csrf-token header). Falls back to the relative path
// when no API URL is configured (local dev).
const SURF_API_BASE = process.env.REACT_APP_API_URL || '';
const SURF_AGENT_URL = SURF_API_BASE
  ? (SURF_API_BASE.includes('/api') ? `${SURF_API_BASE}/surf/agent` : `${SURF_API_BASE}/api/surf/agent`)
  : '/api/surf/agent';

// Pages where Surf already runs his own experience — don't double up.
const ROUTES_SUPPRESSED = ['/onboarding', '/onboarding-v2', '/onboarding/legacy', '/login', '/signup'];

export default function SurfWidget() {
  const location = useLocation();
  const { user } = useAuth();
  const suppressed = ROUTES_SUPPRESSED.some((p) => location.pathname.startsWith(p));

  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState('closed'); // closed | summoning | open | dismissing

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [foxState, setFoxState] = useState('idle'); // idle | thinking | speaking

  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);
  const seededRef = useRef(false);
  // Raw Anthropic-format conversation (includes tool_use / tool_result blocks),
  // kept separate from the display `messages`. The agent loop needs the prior
  // tool results replayed verbatim, or it loses e.g. which phone numbers a
  // search returned and re-searches forever instead of purchasing the pick.
  const conversationRef = useRef([]);
  // Server-issued id that ties every turn of this chat to one stored transcript
  // (internal record). Null until the first response; cleared on new-chat.
  const conversationIdRef = useRef(null);
  // When set, Surf is in focused "recalibrate" mode for one campaign: every
  // /agent call carries mode + campaign id, and the backend swaps to the
  // single-goal recalibrate prompt + tools. Cleared on close / new-chat.
  const recalibrateRef = useRef(null); // { campaignId, campaignName } | null

  // Auto-scroll on new messages
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Keep the input focused: when the panel finishes opening AND every time Surf
  // returns to idle (after thinking/streaming). The input is disabled while Surf
  // works, which blurs it, so without this the tenant has to click back into the
  // box before every message. Refocusing on idle lets them just keep typing.
  useEffect(() => {
    if (phase === 'open' && foxState === 'idle' && !isTyping) {
      inputRef.current?.focus();
    }
  }, [phase, foxState, isTyping]);

  // Seed the opener the first time the widget is opened in this session
  useEffect(() => {
    if (phase !== 'open') return;
    if (seededRef.current) return;
    seededRef.current = true;
    setMessages([{ role: 'assistant', text: HARDCODED_OPENER, streaming: false }]);
  }, [phase]);

  // Esc closes the overlay
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Allow other parts of the app to summon Surf programmatically (e.g. the
  // "+ Create Campaign" CTA on /campaigns now opens Surf instead of a modal).
  useEffect(() => {
    const onOpen = () => { if (!isOpen) handleOpen(); };
    window.addEventListener('surf:open', onOpen);
    return () => window.removeEventListener('surf:open', onOpen);
  }, [isOpen]);

  // Launch Surf in focused "recalibrate" mode for a specific campaign — fired by
  // a "Recalibrate with Surf" button on the campaign view via
  // window.dispatchEvent(new CustomEvent('surf:recalibrate', { detail: { campaignId, campaignName } })).
  useEffect(() => {
    const onRecal = (e) => {
      const { campaignId, campaignName } = e.detail || {};
      if (!campaignId) return;
      const name = campaignName || 'this campaign';
      recalibrateRef.current = { campaignId, campaignName: name };
      conversationRef.current = [];
      conversationIdRef.current = null;
      seededRef.current = true; // suppress the default general opener effect
      setMessages([{
        role: 'assistant',
        text: `Let's recalibrate ${name}. What feels off about how it's reaching people? Tell me what to change and I'll rework the messaging, show you the before and after, and keep the old version so it's reversible.`,
        streaming: false,
      }]);
      setInput('');
      setFoxState('idle');
      setIsTyping(false);
      if (!isOpen) handleOpen();
    };
    window.addEventListener('surf:recalibrate', onRecal);
    return () => window.removeEventListener('surf:recalibrate', onRecal);
  }, [isOpen]);

  function handleOpen() {
    setIsOpen(true);
    setPhase('summoning');
    // Match the entrance animation duration
    setTimeout(() => setPhase('open'), 800);
  }

  function handleClose() {
    // Closing ends any focused recalibrate session — reopening the fox is
    // general Surf again.
    recalibrateRef.current = null;
    setPhase('dismissing');
    setTimeout(() => {
      setIsOpen(false);
      setPhase('closed');
    }, 500);
  }

  async function streamAssistant(text) {
    setFoxState('thinking');
    setIsTyping(true);
    await sleep(TYPING_BEFORE_MS);
    setIsTyping(false);
    setFoxState('speaking');
    setMessages((prev) => [...prev, { role: 'assistant', text: '', streaming: true }]);
    for (let i = 1; i <= text.length; i++) {
      await sleep(CHAR_STREAM_MS);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], text: text.slice(0, i) };
        return next;
      });
    }
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], streaming: false };
      return next;
    });
    setFoxState('idle');
  }

  async function callAgent(userText) {
    // Send the authoritative conversation (with tool turns) plus the new user
    // message, NOT a re-mapping of the display bubbles. The backend echoes back
    // an updated `conversation` we persist for the next turn.
    const outbound = [...conversationRef.current, { role: 'user', content: userText }];
    const body = { messages: outbound, conversation_id: conversationIdRef.current };
    if (recalibrateRef.current?.campaignId) {
      body.mode = 'recalibrate';
      body.recalibrate_campaign_id = recalibrateRef.current.campaignId;
    }
    // Bound the wait. Without this the fox sits in `thinking` indefinitely until
    // the browser or an intermediate proxy kills the socket, which is what makes a
    // slow turn look identical to a crash. Fail on our own terms with a message
    // that says what happened.
    //
    // 240s comes from measurement: a commit turn (agent call, talk track
    // generation, Opus quality sweep, DB writes, all sequential) timed at ~75s,
    // and one stalled Claude call inside it can add 120s more. Must stay in step
    // with SURF_TURN_BUDGET_MS in src/api_routes/surfAgent.js.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 240000);
    let res;
    try {
      res = await fetchWithCsrf(SURF_AGENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        const timeoutErr = new Error('agent timed out after 150s');
        timeoutErr.userMessage = 'That one took too long and Surf stopped waiting. If you were applying a change, check the campaign before trying again in a smaller step.';
        throw timeoutErr;
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      // The server sends a tenant-safe `message` on the failures it understands
      // (a stuck loop, a refused apply). Carry it up so the tenant sees what
      // actually happened instead of a blanket "lost the connection", which
      // reads as a flaky network and hides a real, repeatable server error.
      let friendly = null;
      try { friendly = JSON.parse(detail)?.message || null; } catch { /* not JSON */ }
      const err = new Error(`agent failed: ${res.status} ${detail.slice(0, 200)}`);
      err.userMessage = friendly;
      throw err;
    }
    const data = await res.json();
    if (Array.isArray(data.conversation)) conversationRef.current = data.conversation;
    if (data.conversation_id) conversationIdRef.current = data.conversation_id;
    return data;
  }

  // Wipes the conversation and re-seeds the hardcoded opener. Used by the
  // RotateCcw button in the header. Doesn't close the panel — the tenant
  // stays in the widget, just at a fresh start.
  function handleNewChat() {
    if (foxState !== 'idle' || isTyping) return;
    recalibrateRef.current = null; // new chat returns to general Surf
    conversationRef.current = [];
    conversationIdRef.current = null;
    setMessages([{ role: 'assistant', text: HARDCODED_OPENER, streaming: false }]);
    setInput('');
    setFoxState('idle');
    setIsTyping(false);
    // seededRef is already true (we set it on first open) — keep it that
    // way so the opener-seeding useEffect doesn't double-fire on next open.
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text) return;
    if (foxState !== 'idle' || isTyping) return;

    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInput('');
    setFoxState('thinking');
    setIsTyping(true);

    try {
      const resp = await callAgent(text);
      await streamAssistant(resp.message);
    } catch (err) {
      console.error('[SurfWidget] agent failed', err);
      await streamAssistant(err?.userMessage || "Surf lost the connection. Try again in a sec?");
    }
  }

  if (suppressed) return null;
  // Prospect demo: Surf is a permanent docked guide, not the summoned overlay.
  if (user?.is_prospect_demo === true) return <DemoSurfPanel userId={user.id} />;

  return (
    <>
      <style>{INLINE_STYLES}</style>

      {/* No floating launcher. Surf is summoned from the "Surf" wordmark in the
          sidebar footer (and the Campaigns CTA), both via the 'surf:open' event —
          the bottom-right corner was occluding lead-detail content, and a fox that
          floats over the page forever costs more real estate than it earns for a
          tool the tenant summons deliberately. */}

      {/* Summoning UI — a contained side panel (NOT a full-screen takeover).
          The platform stays visible behind a subtle dim so the tenant knows
          where they are; Surf gets the right ~480px as a focused workspace.
          Drama comes from the slide-in, the fox materializing, the cyan
          glow building behind the panel — not from blanket coverage. */}
      {isOpen && (
        <>
          {/* Backdrop — dims the underlying page. Click to close.
              z-[10000] sits above the Layout's z-[9999] modals and any
              sticky page header bars that escape lower z-index overlays.
              Opacity 75 + backdrop-blur so white surfaces read as
              definitively dimmed. */}
          <div
            onClick={handleClose}
            className={`fixed inset-0 z-[10000] bg-slate-950 backdrop-blur-sm transition-opacity duration-500 ${
              phase === 'dismissing' ? 'opacity-0' :
              phase === 'summoning'  ? 'opacity-0' :
                                       'opacity-75'
            }`}
          />

          {/* Cyan rising-glow — emerges from where the button was, wraps
              around the panel's right edge so it feels like the panel was
              summoned with the glow, not bolted on. */}
          <div
            className={`fixed bottom-0 right-0 w-[640px] h-[520px] rounded-full bg-cyan-400 blur-[110px] pointer-events-none transition-all duration-[700ms] ease-out z-[10000] ${
              phase === 'summoning' ? 'opacity-0 scale-50' :
              phase === 'open'      ? 'opacity-35 scale-100' :
              phase === 'dismissing'? 'opacity-0 scale-50' :
                                      'opacity-0 scale-50'
            }`}
          />

          {/* The Surf panel — slides in from the right. SOLID background
              so nothing bleeds through. Width 460px, full height. Fox up
              top with cyan glow, chat below. z-[10001] sits above the
              backdrop AND above every other layout element (including
              the platform's z-[9999] modals), so nothing on any page
              renders over Surf. */}
          <div
            className={`fixed top-0 right-0 bottom-0 z-[10001] w-[460px] bg-slate-950 border-l border-cyan-500/30 shadow-2xl shadow-cyan-500/20 flex flex-col overflow-hidden transition-transform duration-700 ease-out ${
              phase === 'summoning'  ? 'translate-x-full' :
              phase === 'open'       ? 'translate-x-0' :
              phase === 'dismissing' ? 'translate-x-full' :
                                       'translate-x-full'
            }`}
          >
            {/* Atmosphere layer behind the fox section */}
            <div className="absolute inset-0 pointer-events-none">
              <HexBackground />
              <Particles />
            </div>

            {/* Top header bar — left: live indicator + Surf label, right:
                new-chat reset + close X. Reset wipes the conversation and
                re-seeds the opener so the tenant can start fresh without a
                page reload (which the page state otherwise required). */}
            <div className="relative z-10 flex items-center justify-between px-5 py-3 border-b border-slate-800/80 bg-slate-950/90 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${foxState === 'idle' ? 'bg-cyan-400 animate-pulse' : 'bg-cyan-300'}`} />
                <div className="text-xs uppercase tracking-[0.25em] text-cyan-400/80 font-medium">Surf</div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleNewChat}
                  className="text-slate-400 hover:text-slate-200 transition-colors p-1"
                  aria-label="New chat"
                  title="Start a new chat"
                  disabled={foxState !== 'idle' || isTyping}
                >
                  <RotateCcw size={16} />
                </button>
                <button
                  onClick={handleClose}
                  className="text-slate-400 hover:text-slate-200 transition-colors p-1"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Fox section — top ~35% of remaining height */}
            <div className="relative h-[36%] min-h-[260px] flex-shrink-0 overflow-hidden border-b border-slate-800/60">
              <FoxAsset state={foxState} />
            </div>

            {/* Chat section — fills the rest. Fully opaque so the dashboard
                behind doesn't bleed through the bottom half of the panel. */}
            <div className="relative flex-1 flex flex-col bg-slate-900 min-h-0">
              <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.map((m, i) => <ChatMessage key={i} message={m} />)}
                {isTyping && <TypingIndicator />}
              </div>
              <div className="px-4 py-3 border-t border-slate-800">
                <form
                  onSubmit={handleSubmit}
                  className="flex items-center gap-2 bg-slate-800/70 border border-slate-700 focus-within:border-cyan-500/60 rounded-lg px-3 py-2 transition-colors"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Say something to Surf..."
                    className="flex-1 bg-transparent text-slate-100 placeholder-slate-500 text-sm focus:outline-none"
                    autoComplete="off"
                    disabled={foxState !== 'idle' || isTyping}
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || foxState !== 'idle' || isTyping}
                    className="p-1.5 rounded bg-cyan-500 text-slate-950 hover:bg-cyan-400 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
                    aria-label="Send"
                  >
                    <Send size={14} />
                  </button>
                </form>
              </div>
            </div>
          </div>

        </>
      )}
    </>
  );
}

// ─── Inline styles for animations Surf-specific to the widget ─────────────
const INLINE_STYLES = `
@keyframes surf-fox-float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
@keyframes surf-particle-rise { 0% { transform: translateY(0); opacity: 0; } 25% { opacity: 0.75; } 100% { transform: translateY(-140px); opacity: 0; } }
@keyframes surf-glow-idle { 0%,100% { opacity: 0.35; } 50% { opacity: 0.55; } }
.surf-fox-float { animation: surf-fox-float 6s ease-in-out infinite; }
.surf-particle { animation: surf-particle-rise linear infinite; }
.surf-glow-idle { animation: surf-glow-idle 4s ease-in-out infinite; }
`;

function FoxAsset({ state }) {
  const isThinking = state === 'thinking';
  const isSpeaking = state === 'speaking';

  const glowMain = isSpeaking
    ? 'opacity-100 scale-125'
    : isThinking
      ? 'opacity-80 scale-110'
      : 'opacity-45 scale-100';

  return (
    <div className="relative w-full h-full">
      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[420px] h-40 rounded-full bg-cyan-500 blur-[80px] transition-all duration-700 ease-out pointer-events-none ${glowMain} ${state === 'idle' ? 'surf-glow-idle' : ''}`} />
      <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 w-52 h-24 rounded-full bg-cyan-300 blur-3xl transition-all duration-500 pointer-events-none ${glowMain}`} />
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-64 h-px bg-gradient-to-r from-transparent via-cyan-300/70 to-transparent pointer-events-none" />
      <div className="absolute inset-0 flex justify-center items-end pointer-events-none">
        <img
          src="/full_body_sitting.png"
          alt="Surf"
          className="h-auto max-h-full w-auto max-w-full object-contain surf-fox-float"
          style={{ filter: 'drop-shadow(0 0 12px rgba(0, 200, 180, 0.45))' }}
        />
      </div>
    </div>
  );
}

function ChatMessage({ message }) {
  if (message.role === 'assistant') {
    return (
      <div className="flex items-start gap-3 max-w-[88%]">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold text-xs">S</div>
        <div className="rounded-2xl rounded-tl-sm bg-slate-800/80 border border-slate-700/50 px-4 py-3 text-slate-100 leading-relaxed">
          <span>{message.text}</span>
          {message.streaming && (
            <span className="inline-block w-1.5 h-4 bg-cyan-400 ml-1 align-middle animate-pulse" />
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-end">
      <div className="rounded-2xl rounded-tr-sm bg-cyan-500/15 border border-cyan-500/30 px-4 py-3 text-slate-100 max-w-[80%] leading-relaxed">
        {message.text}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold text-xs">S</div>
      <div className="rounded-2xl rounded-tl-sm bg-slate-800/80 border border-slate-700/50 px-4 py-3">
        <div className="flex gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '160ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '320ms' }} />
        </div>
      </div>
    </div>
  );
}

// ─── Atmosphere (HexBackground + Particles, copied from onboarding) ───────
function HexBackground() {
  return (
    <svg className="absolute inset-0 w-full h-full text-cyan-500/15 pointer-events-none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="surf-hex-grid" width="48" height="42" patternUnits="userSpaceOnUse">
          <path d="M12 0 L36 0 L48 21 L36 42 L12 42 L0 21 Z" fill="none" stroke="currentColor" strokeWidth="0.6" />
        </pattern>
        <radialGradient id="surf-hex-fade" cx="50%" cy="50%" r="65%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <mask id="surf-hex-mask">
          <rect width="100%" height="100%" fill="url(#surf-hex-fade)" />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="url(#surf-hex-grid)" mask="url(#surf-hex-mask)" />
    </svg>
  );
}

function Particles() {
  const items = Array.from({ length: 18 }, (_, i) => ({
    left: (i * 37) % 100,
    bottom: -10 + ((i * 17) % 30),
    delay: (i * 0.5) % 6,
    dur: 7 + (i % 5),
    size: i % 3 === 0 ? 2 : 1,
  }));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {items.map((p, i) => (
        <div
          key={i}
          className="surf-particle absolute rounded-full bg-cyan-300/60"
          style={{
            left: `${p.left}%`,
            bottom: `${p.bottom}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
          }}
        />
      ))}
    </div>
  );
}
